import type { LoyaltyTier } from "@station/contracts";
import { Decimal } from "@station/core";
import { Alert, Button, DataTable, type DataTableColumn, EmptyState } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Medal, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPoints } from "../../../features/loyalty/loyalty-values.js";
import { useLoyaltyPermissions, useLoyaltyTiers } from "../../../features/loyalty/queries.js";
import { ActiveBadge, ColorSwatch, LoyaltyPage } from "../../../features/loyalty/shared.js";
import { TierDialog } from "../../../features/loyalty/tier-dialog.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/sadakat/seviyeler")({ component: TiersRoute });

/**
 * Sadakat seviyeleri (ADR 0017 §21): sıraya göre (alttan üste) liste; eşik kayan penceredeki puandır, çarpan kazanımı
 * büyütür. Yükselme kazanımda hemen, düşme gece değerlendirmesinde. Seviye kodu kampanyadaki `LOYALTY_TIER` koşuludur.
 */
function TiersRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const [editing, setEditing] = useState<{ tier: LoyaltyTier | null } | null>(null);
  // Yeni seviyenin varsayılan sırası mevcut seviyelerden hesaplanır: liste gelmeden pencere açılmaz (yoksa sıra 1 önerilir
  // ve etkin 1. seviyeyle çakışır — yavaş / hız sınırına takılan istekte görülen yarış).
  const tiers = useLoyaltyTiers(permissions.enabled && permissions.canRead);
  return (
    <LoyaltyPage
      title={t("menu.loyaltyTiers")}
      description={t("loyalty.tiers.description")}
      actions={
        permissions.canManage ? (
          <Button onClick={() => setEditing({ tier: null })} disabled={!tiers.data}>
            <Plus aria-hidden="true" />
            {t("loyalty.tiers.create")}
          </Button>
        ) : null
      }
    >
      <TiersBody onEdit={(tier) => setEditing({ tier })} editing={editing} onClose={() => setEditing(null)} />
    </LoyaltyPage>
  );
}

function TiersBody({
  onEdit,
  editing,
  onClose,
}: {
  onEdit: (tier: LoyaltyTier) => void;
  editing: { tier: LoyaltyTier | null } | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const tiers = useLoyaltyTiers();
  const rows = useMemo(
    () => [...(tiers.data ?? [])].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.rank - b.rank),
    [tiers.data],
  );

  const columns = useMemo<DataTableColumn<LoyaltyTier>[]>(
    () => [
      {
        id: "rank",
        header: t("loyalty.tiers.fields.rank"),
        align: "right",
        hideable: false,
        exportValue: (row) => row.rank,
        cell: (row) => <span className="tabular">{row.rank}</span>,
      },
      {
        id: "code",
        header: t("loyalty.tiers.fields.code"),
        exportValue: (row) => row.code,
        cell: (row) => <span className="font-mono">{row.code}</span>,
      },
      {
        id: "name",
        header: t("loyalty.tiers.fields.name"),
        exportValue: (row) => row.name,
        cell: (row) => row.name,
      },
      {
        id: "threshold",
        header: t("loyalty.tiers.fields.thresholdPoints"),
        align: "right",
        exportValue: (row) => row.thresholdPoints,
        cell: (row) => (
          <span className="tabular whitespace-nowrap">
            {t("loyalty.pointsUnit", { points: formatPoints(row.thresholdPoints) })}
          </span>
        ),
      },
      {
        id: "multiplier",
        header: t("loyalty.tiers.fields.earnMultiplier"),
        align: "right",
        exportValue: (row) => row.earnMultiplier,
        cell: (row) => <span className="tabular">× {Decimal.from(row.earnMultiplier).formatTr(2)}</span>,
      },
      {
        id: "color",
        header: t("loyalty.tiers.fields.color"),
        exportValue: (row) => row.color,
        cell: (row) => <ColorSwatch color={row.color} />,
      },
      {
        id: "benefits",
        header: t("loyalty.tiers.fields.benefits"),
        exportValue: (row) => row.benefits,
        cell: (row) =>
          row.benefits ? (
            <span className="line-clamp-2 max-w-72">{row.benefits}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "members",
        header: t("loyalty.tiers.members"),
        align: "right",
        exportValue: (row) => row.memberCount,
        cell: (row) => <span className="tabular">{formatCount(row.memberCount)}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
        cell: (row) => <ActiveBadge active={row.isActive} />,
      },
      {
        id: "actions",
        header: t("common.actions"),
        cell: (row) =>
          permissions.canManage ? (
            <Button variant="outline" size="sm" className="relative" onClick={() => onEdit(row)}>
              {t("common.edit")}
              <span className="sr-only"> — {row.name}</span>
            </Button>
          ) : null,
      },
    ],
    [t, permissions.canManage, onEdit],
  );

  return (
    <>
      <DataTable
        caption={t("loyalty.tiers.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={tiers.isPending}
        error={tiers.error ? <Alert tone="danger">{errorMessage(tiers.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Medal />}
            title={t("loyalty.tiers.emptyTitle")}
            description={t("loyalty.tiers.emptyDescription")}
          />
        }
      />
      <p className="text-xs text-muted-foreground">{t("loyalty.tiers.footnote")}</p>
      <TierDialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        tier={editing?.tier ?? null}
        tiers={tiers.data ?? []}
      />
    </>
  );
}
